import { useCallback, useEffect, useRef } from "react";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import {
  CoverageBetaTrainingOverlay,
  type TrainingStep,
} from "./CoverageBetaTrainingOverlay";
import {
  COVERAGE_BETA_TRAINING_STEPS,
  COVERAGE_BETA_TRAINING_STORAGE_KEY,
} from "./coverage-beta-training-steps";
import { readTutorialState, writeTutorialState } from "./tutorial-storage";

export interface CoverageBetaTrainingProps {
  /**
   * True once the parcel + query image have resolved — gates auto-open so
   * the tour never opens over a gray box. See CoverageBetaTask's `ready`
   * computation (parcelResp/parcelIsLoading plus the query image's own
   * fetch, via useFetchGalleryImgUrl).
   */
  ready: boolean;
  /**
   * The current task's id. A change (Submit optimistically swapping in the
   * prefetched next task, or a next-pending refetch) force-closes an open
   * tour without persisting — see the task-change effect below.
   */
  taskId: string;
  /**
   * Benchmark quiz tasks: suppress auto-open (still replayable via the
   * header's help icon) — the tour would skew the quiz's scored timing
   * metrics (avg_seconds_per_task / tasks_per_hour).
   */
  autoOpenDisabled?: boolean;
}

/**
 * Hosts the Coverage Beta onboarding tutorial: decides when to auto-open it,
 * drives `rightPanelView` per step so a step's anchor actually exists to
 * spotlight, restores that view on close, and persists dismissal. Mounted
 * once from CoverageBetaTask — labeler view only, never admin or QA (see
 * the render site there for why).
 */
export const CoverageBetaTraining = (props: CoverageBetaTrainingProps) => {
  const { ready, taskId, autoOpenDisabled = false } = props;
  const tutorialActive = useCoverageBetaStore((s) => s.tutorialActive);

  // What to restore once the tour closes — captured the instant it opens
  // (however it opened: auto-open below, or the header's replay button),
  // regardless of which caused it. Only `rightPanelView` is driven today;
  // `activeMode`/`confirming`/`activeEmptyAreaIdx` are deliberately never
  // touched by the tutorial (see coverage-beta-training-steps.tsx and the
  // plan doc) so there is nothing else to snapshot.
  const snapshotRef = useRef<{
    rightPanelView: "shelf-images" | "planogram";
  } | null>(null);

  // Deps are exactly [tutorialActive], so this effect only re-runs when the
  // value actually flips — meaning whenever it runs with tutorialActive
  // true, that's always a fresh open (no separate "was it already open"
  // guard needed).
  useEffect(() => {
    if (tutorialActive) {
      snapshotRef.current = {
        rightPanelView: useCoverageBetaStore.getState().rightPanelView,
      };
    }
  }, [tutorialActive]);

  // `result` is null for a forced close (the task changed while the tour
  // was open) — that path restores state but never persists, since
  // dismissing the tour was never actually the labeler's decision there.
  const closeTutorial = useCallback(
    (result: { completed: boolean; lastStep: number } | null) => {
      const snap = snapshotRef.current;
      if (snap) {
        useCoverageBetaStore.getState().setRightPanelView(snap.rightPanelView);
        snapshotRef.current = null;
      }
      if (result) {
        writeTutorialState(
          window.localStorage,
          COVERAGE_BETA_TRAINING_STORAGE_KEY,
          result,
        );
      }
      useCoverageBetaStore.getState().setTutorialActive(false);
    },
    [],
  );

  // Auto-open once ready, unless disabled (benchmark quiz) or already seen.
  // Guarded to fire at most once per mount — this page swaps tasks without
  // unmounting (see CoverageBetaSubmitBtn), and re-considering auto-open on
  // every task flip would just reopen the tour right after the task-change
  // effect below force-closes it.
  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (!ready || didAutoOpenRef.current) return;
    didAutoOpenRef.current = true;
    if (autoOpenDisabled) return;
    if (
      readTutorialState(window.localStorage, COVERAGE_BETA_TRAINING_STORAGE_KEY)
        .seen
    ) {
      return;
    }
    useCoverageBetaStore.getState().setTutorialActive(true);
  }, [ready, autoOpenDisabled]);

  // The task can change under this component without it unmounting (Submit
  // optimistically swaps in the prefetched next task's data; a
  // next-pending refetch can also reassign the task). An open tour's
  // snapshot and resolved anchors describe the OLD task, so force-close
  // rather than let it dangle over the new one — but don't persist (the
  // labeler never chose to dismiss it) and don't let it auto-reopen for the
  // new task (see didAutoOpenRef above).
  const prevTaskIdRef = useRef(taskId);
  useEffect(() => {
    if (prevTaskIdRef.current === taskId) return;
    prevTaskIdRef.current = taskId;
    if (useCoverageBetaStore.getState().tutorialActive) {
      closeTutorial(null);
    }
  }, [taskId, closeTutorial]);

  const handleStepChange = useCallback((step: TrainingStep) => {
    const s = useCoverageBetaStore.getState();
    if (step.id === "shelf-images" || step.id === "no-match") {
      s.setRightPanelView("shelf-images");
    } else if (step.id === "planogram") {
      s.setRightPanelView("planogram");
    }
  }, []);

  return (
    <CoverageBetaTrainingOverlay
      active={tutorialActive}
      steps={COVERAGE_BETA_TRAINING_STEPS}
      onStepChange={handleStepChange}
      onClose={closeTutorial}
    />
  );
};
